import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { sendCommand } from '../lib/extension'
import { db } from '../lib/db'
import ComposeBar from './ComposeBar'

interface Recipient {
  name: string
  headline: string
  profileUrn: string
  profileImage: string | null
}

interface ComposeViewProps {
  initialRecipient?: Recipient | null
  onSent: (conversationUrn: string, recipients: Recipient[]) => void
  onClose: () => void
}

const SEARCH_DEBOUNCE = 300

export default function ComposeView({ initialRecipient, onSent, onClose }: ComposeViewProps) {
  const [selectedRecipients, setSelectedRecipients] = useState<Recipient[]>(
    initialRecipient ? [initialRecipient] : []
  )
  const [recipientQuery, setRecipientQuery] = useState('')
  const [searchResults, setSearchResults] = useState<Recipient[]>([])
  const [recentConnections, setRecentConnections] = useState<Recipient[]>([])
  const [connectionsLoading, setConnectionsLoading] = useState(true)
  const [searchLoading, setSearchLoading] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(!initialRecipient)
  const [highlightIndex, setHighlightIndex] = useState(0)

  const searchInputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Autofocus: search input if no recipient, otherwise let ComposeBar handle focus
  useEffect(() => {
    if (!initialRecipient) searchInputRef.current?.focus()
  }, [initialRecipient])

  // Fetch recent connections from LinkedIn API on mount
  // Then enrich missing profile images from local IndexedDB conversation cache
  useEffect(() => {
    setConnectionsLoading(true)
    sendCommand<{ connections: Recipient[] }>('FETCH_RECENT_CONNECTIONS', { count: 20 })
      .then(async (result) => {
        const arr = Array.isArray(result?.connections) ? result.connections : []
        // Fill in missing profile images from cached conversations
        const enriched = await Promise.all(
          arr.map(async (r) => {
            if (r.profileImage) return r
            const conv = await db.conversations
              .where('participantProfileUrn')
              .equals(r.profileUrn)
              .first()
            return conv?.participantProfileImage
              ? { ...r, profileImage: conv.participantProfileImage }
              : r
          })
        )
        setRecentConnections(enriched)
      })
      .catch(() => {})
      .finally(() => setConnectionsLoading(false))
  }, [])

  // Debounced peer search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (!recipientQuery.trim()) {
      setSearchResults([])
      setSearchLoading(false)
      return
    }

    setSearchLoading(true)
    debounceRef.current = setTimeout(() => {
      sendCommand<{ peers: Recipient[] }>('FETCH_PEER_SEARCH', {
        keywords: recipientQuery,
        count: 20,
      })
        .then((result) => {
          const arr = result?.peers
          setSearchResults(Array.isArray(arr) ? arr : [])
        })
        .catch(() => {})
        .finally(() => setSearchLoading(false))
    }, SEARCH_DEBOUNCE)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [recipientQuery])

  // Build dropdown items: filter out already-selected recipients
  const selectedUrns = useMemo(
    () => new Set(selectedRecipients.map((r) => r.profileUrn)),
    [selectedRecipients]
  )

  const dropdownItems = useMemo(() => {
    const source = recipientQuery.trim() ? searchResults : recentConnections
    if (!Array.isArray(source)) return []
    return source.filter((r) => r && !selectedUrns.has(r.profileUrn))
  }, [recipientQuery, searchResults, recentConnections, selectedUrns])

  // Show skeletons while initial connections load or while search is in flight
  const isLoading = recipientQuery.trim() ? searchLoading : connectionsLoading

  // Reset highlight when dropdown items change
  useEffect(() => {
    setHighlightIndex(0)
  }, [dropdownItems])

  // Existing conversation detection: when a recipient is selected who already has a
  // conversation, navigate directly to the full ThreadView instead of showing a
  // simplified inline renderer.
  //
  // Cancellation guard (POST_FIX_ASTAR_AUDIT_HANDOFF finding #8):
  // the Dexie lookup is async. If the user swaps recipients (A → B)
  // before A's lookup resolves, A's .then could fire onSent with A's
  // conversation URN even though recipients now point at B — user
  // lands in the wrong thread. The `cancelled` flag short-circuits
  // stale results.
  //
  // We also capture `selectedRecipients` at effect-start so the
  // onSent call reflects the state that INITIATED the lookup, not
  // whatever the ref happens to point at by the time the promise
  // resolves (belt-and-braces against the React-closure case as well
  // as the async race case).
  useEffect(() => {
    if (selectedRecipients.length === 0) {
      return
    }

    let cancelled = false
    const recipientsAtStart = selectedRecipients

    const detect = async () => {
      if (recipientsAtStart.length === 1) {
        const match = await db.conversations
          .where('participantProfileUrn')
          .equals(recipientsAtStart[0].profileUrn)
          .first()
        if (cancelled) return
        if (match && !match.isGroup) {
          onSent(match.conversationUrn, recipientsAtStart) // exit compose, open full ThreadView
          return
        }
      } else {
        const targetUrns = new Set(recipientsAtStart.map((r) => r.profileUrn))
        const all = await db.conversations.toArray()
        if (cancelled) return
        const match = all.find((c) => {
          if (!c.isGroup || !c.allParticipants) return false
          const convUrns = new Set(
            c.allParticipants.map((p) => p.profileUrn).filter(Boolean)
          )
          if (convUrns.size !== targetUrns.size) return false
          for (const urn of targetUrns) {
            if (!convUrns.has(urn)) return false
          }
          return true
        })
        if (match) {
          onSent(match.conversationUrn, recipientsAtStart)
          return
        }
      }
    }

    detect()
    return () => { cancelled = true }
  }, [selectedRecipients, onSent])

  const addRecipient = useCallback((recipient: Recipient) => {
    setSelectedRecipients((prev) => {
      if (prev.some((r) => r.profileUrn === recipient.profileUrn)) return prev
      return [...prev, recipient]
    })
    setRecipientQuery('')
    setDropdownOpen(false)
    searchInputRef.current?.focus()
  }, [])

  const removeRecipient = useCallback((profileUrn: string) => {
    setSelectedRecipients((prev) => prev.filter((r) => r.profileUrn !== profileUrn))
  }, [])

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (dropdownOpen) {
        setDropdownOpen(false)
      } else {
        onClose()
      }
      return
    }
    if (e.key === 'Backspace' && !recipientQuery && selectedRecipients.length > 0) {
      removeRecipient(selectedRecipients[selectedRecipients.length - 1].profileUrn)
      return
    }
    if (!dropdownOpen || dropdownItems.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIndex((i) => Math.min(i + 1, dropdownItems.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (dropdownItems[highlightIndex]) {
        addRecipient(dropdownItems[highlightIndex])
      }
    }
  }

  const participantUrns = useMemo(
    () => selectedRecipients.map((r) => `urn:li:fsd_profile:${r.profileUrn}`),
    [selectedRecipients]
  )

  const handleSent = useCallback(
    (info?: { clientId: string; confirmedUrn?: string; conversationUrn?: string }) => {
      if (info?.conversationUrn) {
        onSent(info.conversationUrn, selectedRecipients)
      } else {
        console.warn('[Compose] Send succeeded but no conversationUrn returned — polling will pick it up')
      }
    },
    [onSent, selectedRecipients]
  )

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-zinc-100 px-6 py-3.5">
        <h2 className="text-sm font-semibold text-zinc-900">New Message</h2>
        <button
          onClick={onClose}
          className="flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Recipient bar */}
      <div className="relative border-b border-zinc-100 px-6 py-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-medium text-zinc-400">To:</span>
          {selectedRecipients.map((r) => (
            <span
              key={r.profileUrn}
              className="inline-flex items-center gap-1 rounded-full bg-zinc-100 py-0.5 pl-0.5 pr-1 text-xs font-medium text-zinc-700"
            >
              {r.profileImage ? (
                <img src={r.profileImage} alt="" className="h-5 w-5 rounded-full" />
              ) : (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-zinc-300 text-[8px] font-semibold text-white">
                  {r.name.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()}
                </span>
              )}
              {r.name}
              <button
                onClick={() => removeRecipient(r.profileUrn)}
                className="flex h-4 w-4 items-center justify-center rounded-full text-zinc-400 hover:bg-zinc-200 hover:text-zinc-600"
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </span>
          ))}
          <input
            ref={searchInputRef}
            type="text"
            value={recipientQuery}
            onChange={(e) => {
              setRecipientQuery(e.target.value)
              setDropdownOpen(true)
            }}
            onFocus={() => setDropdownOpen(true)}
            onKeyDown={handleInputKeyDown}
            placeholder={selectedRecipients.length === 0 ? 'Search connections...' : ''}
            className="min-w-[120px] flex-1 bg-transparent text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none"
          />
        </div>

        {/* Recipient dropdown */}
        {dropdownOpen && (dropdownItems.length > 0 || isLoading) && (
          <div
            ref={dropdownRef}
            className="absolute left-0 right-0 top-full z-20 mx-4 max-h-64 overflow-y-auto rounded-xl border border-zinc-200 bg-white shadow-lg"
          >
            {isLoading && dropdownItems.length === 0 ? (
              <div className="px-2 py-1">
                {[0.9, 0.7, 0.5, 0.35, 0.2].map((opacity, i) => (
                  <div key={i} className="flex items-center gap-3 px-2 py-2.5" style={{ opacity }}>
                    <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-zinc-200" />
                    <div className="flex flex-1 flex-col gap-1.5">
                      <div className="h-2.5 animate-pulse rounded bg-zinc-200" style={{ width: `${45 + (i % 3) * 15}%` }} />
                      <div className="h-2 animate-pulse rounded bg-zinc-100" style={{ width: `${60 + (i % 3) * 10}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              dropdownItems.map((r, i) => (
                <button
                  key={r.profileUrn}
                  onMouseDown={(e) => { e.preventDefault(); addRecipient(r) }}
                  onMouseEnter={() => setHighlightIndex(i)}
                  className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                    i === highlightIndex ? 'bg-zinc-50' : ''
                  }`}
                >
                  {r.profileImage ? (
                    <img src={r.profileImage} alt="" className="h-8 w-8 shrink-0 rounded-full" />
                  ) : (
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-200 text-[10px] font-semibold text-zinc-500">
                      {getInitials(r.name)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium text-zinc-900">{r.name}</p>
                    {r.headline && (
                      <p className="truncate text-[11px] text-zinc-500">{r.headline}</p>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* Message area */}
      <div className="flex min-h-0 flex-1 flex-col">
        {selectedRecipients.length > 0 ? (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-xs text-zinc-400">Start a new conversation</p>
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <div className="text-center">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-zinc-200">
                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                <path d="m15 5 4 4" />
              </svg>
              <p className="mt-3 text-xs text-zinc-400">Search for a connection to message</p>
            </div>
          </div>
        )}

        {/* Compose bar — only show when recipients are selected */}
        {selectedRecipients.length > 0 && (
          <ComposeBar
            participantUrns={participantUrns}
            onSent={handleSent}
          />
        )}
      </div>
    </div>
  )
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}
